import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "motion/react";
import { X, Apple } from "lucide-react";
import { Capacitor } from "@capacitor/core";

export function IosInstallBanner() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user is on iOS, not running in native app already, and hasn't dismissed the banner
    const isIos = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isNative = Capacitor.isNativePlatform();
    const isDismissed = localStorage.getItem("hideIosInstallBanner") === "true";

    if (isIos && !isNative && !isDismissed) {
      // Small delay for better UX after page load
      const showTimer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);

      // Auto-dismiss after 10 seconds
      const dismissTimer = setTimeout(() => {
        setIsVisible(false);
      }, 12000); // 2000ms delay + 10000ms duration

      return () => {
        clearTimeout(showTimer);
        clearTimeout(dismissTimer);
      };
    }
  }, []);

  const handleDismiss = () => {
    localStorage.setItem("hideIosInstallBanner", "true");
    setIsVisible(false);
  };

  const handleInstallClick = () => {
    localStorage.setItem("hideIosInstallBanner", "true");
    setIsVisible(false);
    window.open(
      "https://apps.apple.com/au/app/stock-investing-pro/id6789043150",
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="fixed bottom-6 left-6 right-6 md:left-auto md:right-6 md:w-96 bg-zinc-950/95 backdrop-blur-md border border-blue-500/30 rounded-sm p-4 shadow-[0_0_30px_rgba(59,130,246,0.15)] z-[9999] flex gap-4 items-start"
        >
          <div className="w-10 h-10 bg-blue-500/10 border border-blue-500/30 rounded-sm flex items-center justify-center text-blue-400 shrink-0 mt-0.5">
            <Apple size={20} />
          </div>

          <div className="flex-1 min-w-0 pr-4">
            <h4 className="text-zinc-100 text-xs font-bold uppercase tracking-wider mb-1">
              {t("ios_banner.title")}
            </h4>
            <p className="text-zinc-400 text-[11px] leading-relaxed">
              {t("ios_banner.recommendation")}
            </p>
            <div className="mt-3 flex gap-3">
              <button
                onClick={handleInstallClick}
                className="px-3 py-1 bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white text-[10px] font-extrabold uppercase tracking-widest rounded-sm transition-colors cursor-pointer"
              >
                {t("ios_banner.install")}
              </button>
              <button
                onClick={handleDismiss}
                className="px-3 py-1 bg-zinc-900 hover:bg-zinc-800 active:bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-zinc-300 text-[10px] font-extrabold uppercase tracking-widest rounded-sm transition-colors cursor-pointer"
              >
                {t("ios_banner.dismiss")}
              </button>
            </div>
          </div>

          <button
            onClick={handleDismiss}
            className="text-zinc-500 hover:text-zinc-300 p-1 hover:bg-zinc-900 rounded-sm transition-colors absolute top-3 right-3 cursor-pointer"
            aria-label={t("ios_banner.dismiss")}
          >
            <X size={14} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
